import { useMemo } from "react";
import { buildMoneyWeightedReturn, buildTimeWeightedReturn } from "./analytics";
import {
	PORTFOLIO_START_DATE,
	calculateAnnualInterest,
	useDailyQuery,
	useDividendsQuery,
	useInvestmentSummaryQuery,
	useInvestmentsQuery,
} from "./shared";

// Lives outside shared.js on purpose: analytics.js imports shared.js, so putting
// this there would close an import cycle and leave one of the two modules
// holding an uninitialised binding at load time.
//
// Three rates, because they answer three different questions:
//   - money-weighted (XIRR): what YOUR money earned, given when you added it.
//     The honest headline, and the rate goal projections should compound.
//   - time-weighted: how the picks performed, with the timing of contributions
//     divided out. The figure to compare against an index.
//   - CAGR from the first trade: kept only as a fallback. It divides the whole
//     return across the full window even though most capital arrived much later,
//     which understates the return on the money actually at work.
//
// `timeWeighted: false` skips the daily price series -- 40k+ rows the Goals page
// has no other use for.
export const usePortfolioReturns = ({ timeWeighted = true } = {}) => {
	const { data: summary, isLoading: loadingSummary } = useInvestmentSummaryQuery();
	const { data: investments } = useInvestmentsQuery();
	const { data: dividends } = useDividendsQuery();
	const { data: daily } = useDailyQuery({ enabled: timeWeighted });

	return useMemo(() => {
		const totals = summary?.totals || {};
		const currentWorth = totals.currentWorth || 0;
		const totalReturn =
			(totals.realizedPL || 0) + (totals.unrealizedPL || 0) + (totals.dividendIncome || 0);
		const netInvested = (totals.boughtCost || 0) - (totals.soldProceeds || 0);
		const percent = netInvested > 0 ? (totalReturn / netInvested) * 100 : 0;

		const moneyWeighted = buildMoneyWeightedReturn({
			investments,
			dividends,
			currentWorth,
		});
		const timeWeightedReturn = timeWeighted
			? buildTimeWeightedReturn({ investments, daily, dividends })
			: null;

		const cagrPercent = parseFloat(
			calculateAnnualInterest(percent, PORTFOLIO_START_DATE)
		);

		return {
			isLoading: loadingSummary,
			currentWorth,
			totalReturn,
			netInvested,
			percent,
			cagrPercent,
			moneyWeighted,
			timeWeighted: timeWeightedReturn,
			// A portfolio with one trade, or one opened today, has no solvable IRR.
			// Falling back to CAGR keeps every projection that compounds this rate
			// from silently becoming a projection at 0%/yr.
			projectionPercent:
				moneyWeighted.percent === null ? cagrPercent : moneyWeighted.percent,
			usesMoneyWeighted: moneyWeighted.percent !== null,
		};
	}, [daily, dividends, investments, loadingSummary, summary, timeWeighted]);
};
